/* eslint-disable react-refresh/only-export-components -- Context modules intentionally export provider + hooks. */
import {
  QueuedFile,
  UploadError,
  UploadQueueAction,
  UploadQueueContextType,
  UploadQueueState,
} from '@/types/uploadQueue';
import {
  calculateQueueStats,
  calculateTotalProgress,
  generateFileId,
} from '@/utils/uploadQueueUtils';

import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
} from 'react';

// Initial state
const initialState: UploadQueueState = {
  files: [],
  isUploading: false,
  currentUploadIndex: 0,
  totalProgress: 0,
  errors: [],
  isPaused: false,
  statusChangeCount: 0,
};

// Reducer function
const uploadQueueReducer = (
  state: UploadQueueState,
  action: UploadQueueAction
): UploadQueueState => {
  switch (action.type) {
    case 'ADD_FILES': {
      const { files } = action.payload;
      const newFiles: QueuedFile[] = files.map(file => ({
        id: generateFileId(file),
        file,
        status: 'pending',
        progress: 0,
        // study_id no longer needed as it's now in JWT token
        addedAt: new Date(),
      }));

      const updatedFiles = [...state.files, ...newFiles];

      return {
        ...state,
        files: updatedFiles,
        totalProgress: calculateTotalProgress(updatedFiles),
        statusChangeCount: state.statusChangeCount + 1,
      };
    }

    case 'REMOVE_FILE': {
      const { fileId } = action.payload;
      const updatedFiles = state.files.filter(file => file.id !== fileId);

      return {
        ...state,
        files: updatedFiles,
        totalProgress: calculateTotalProgress(updatedFiles),
        errors: state.errors.filter(error => error.fileId !== fileId),
        statusChangeCount: state.statusChangeCount + 1,
      };
    }

    case 'CLEAR_QUEUE': {
      return {
        ...initialState,
      };
    }

    case 'START_UPLOAD': {
      return {
        ...state,
        isUploading: true,
        isPaused: false,
      };
    }

    case 'PAUSE_UPLOAD': {
      return {
        ...state,
        isUploading: false,
        isPaused: true,
      };
    }

    case 'CANCEL_UPLOAD': {
      const updatedFiles = state.files.map(file =>
        file.status === 'uploading' || file.status === 'pending'
          ? { ...file, status: 'cancelled' as const }
          : file
      );

      return {
        ...state,
        files: updatedFiles,
        isUploading: false,
        isPaused: false,
        currentUploadIndex: 0,
        totalProgress: calculateTotalProgress(updatedFiles),
        statusChangeCount: state.statusChangeCount + 1,
      };
    }

    case 'UPDATE_FILE_PROGRESS': {
      const { fileId, progress } = action.payload;
      const idx = state.files.findIndex(f => f.id === fileId);
      if (idx === -1) return state;
      const files = [...state.files];
      files[idx] = { ...files[idx]!, progress };

      return {
        ...state,
        files,
        totalProgress: calculateTotalProgress(files),
        statusChangeCount: state.statusChangeCount + 1,
      };
    }

    case 'UPDATE_FILE_STATUS': {
      const { fileId, status, error } = action.payload;
      const idx = state.files.findIndex(f => f.id === fileId);
      if (idx === -1) return state;
      const prev = state.files[idx]!;
      const files = [...state.files];
      files[idx] = {
        ...prev,
        status,
        error,
        progress: status === 'completed' ? 100 : prev.progress,
        uploadedAt: status === 'completed' ? new Date() : prev.uploadedAt,
      };

      return {
        ...state,
        files,
        totalProgress: calculateTotalProgress(files),
        statusChangeCount: state.statusChangeCount + 1,
      };
    }

    case 'SET_CURRENT_UPLOAD_INDEX': {
      return {
        ...state,
        currentUploadIndex: action.payload.index,
      };
    }

    case 'ADD_ERROR': {
      return {
        ...state,
        errors: [...state.errors, action.payload],
      };
    }

    case 'RETRY_FILE': {
      const { fileId } = action.payload;
      const idx = state.files.findIndex(f => f.id === fileId && f.status === 'error');
      if (idx === -1) return state;
      const files = [...state.files];
      files[idx] = {
        ...files[idx]!,
        status: 'pending',
        progress: 0,
        error: undefined,
      };

      return {
        ...state,
        files,
        errors: state.errors.filter(error => error.fileId !== fileId),
        totalProgress: calculateTotalProgress(files),
        statusChangeCount: state.statusChangeCount + 1,
      };
    }

    case 'RETRY_ALL_ERRORS': {
      const errorFiles = state.files.filter(f => f.status === 'error');
      const otherFiles = state.files.filter(f => f.status !== 'error');
      const retriedAsPending = errorFiles.map(f => ({
        ...f,
        status: 'pending' as const,
        progress: 0,
        error: undefined,
      }));
      const updatedFiles = [...otherFiles, ...retriedAsPending];

      return {
        ...state,
        files: updatedFiles,
        errors: [],
        totalProgress: calculateTotalProgress(updatedFiles),
        statusChangeCount: state.statusChangeCount + 1,
      };
    }

    default:
      return state;
  }
};

// Create context
const UploadQueueContext = createContext<UploadQueueContextType | undefined>(
  undefined
);

// Context provider component
interface UploadQueueProviderProps {
  children: React.ReactNode;
  initialState?: Partial<UploadQueueState>;
}

export const UploadQueueProvider: React.FC<UploadQueueProviderProps> = ({
  children,
  initialState: providedInitialState,
}) => {
  const [state, dispatch] = useReducer(uploadQueueReducer, {
    ...initialState,
    ...providedInitialState,
  });

  // Action creators
  const addFiles = useCallback((files: File[]) => {
    dispatch({ type: 'ADD_FILES', payload: { files } });
  }, []);

  const removeFile = useCallback((fileId: string) => {
    dispatch({ type: 'REMOVE_FILE', payload: { fileId } });
  }, []);

  const clearQueue = useCallback(() => {
    dispatch({ type: 'CLEAR_QUEUE' });
  }, []);

  const startUpload = useCallback(() => {
    dispatch({ type: 'START_UPLOAD' });
  }, []);

  const pauseUpload = useCallback(() => {
    dispatch({ type: 'PAUSE_UPLOAD' });
  }, []);

  const cancelUpload = useCallback(() => {
    dispatch({ type: 'CANCEL_UPLOAD' });
  }, []);

  const retryFile = useCallback((fileId: string) => {
    dispatch({ type: 'RETRY_FILE', payload: { fileId } });
  }, []);

  const retryAllErrors = useCallback(() => {
    dispatch({ type: 'RETRY_ALL_ERRORS' });
  }, []);

  const getStats = useCallback(() => {
    return calculateQueueStats(state.files);
  }, [state.files]);

  // Internal dispatch functions (for provider use)
  const updateFileProgress = useCallback((fileId: string, progress: number) => {
    dispatch({ type: 'UPDATE_FILE_PROGRESS', payload: { fileId, progress } });
  }, []);

  const updateFileStatus = useCallback(
    (fileId: string, status: QueuedFile['status'], error?: string) => {
      dispatch({
        type: 'UPDATE_FILE_STATUS',
        payload: { fileId, status, error },
      });
    },
    []
  );

  const setCurrentUploadIndex = useCallback((index: number) => {
    dispatch({ type: 'SET_CURRENT_UPLOAD_INDEX', payload: { index } });
  }, []);

  const addError = useCallback((error: UploadError) => {
    dispatch({ type: 'ADD_ERROR', payload: error });
  }, []);

  const contextValue: UploadQueueContextType = {
    state,
    addFiles,
    removeFile,
    clearQueue,
    startUpload,
    pauseUpload,
    cancelUpload,
    retryFile,
    retryAllErrors,
    getStats,
    // Developer Tools methods
    updateFileProgress,
    updateFileStatus,
    addError,
    setCurrentUploadIndex,
  };

  // Remove the extended context workaround since methods are now public
  return (
    <UploadQueueContext.Provider value={contextValue}>
      {children}
    </UploadQueueContext.Provider>
  );
};

// Custom hook to use upload queue context
export const useUploadQueue = (): UploadQueueContextType => {
  const context = useContext(UploadQueueContext);
  if (context === undefined) {
    throw new Error(
      'useUploadQueue must be used within an UploadQueueProvider'
    );
  }
  return context;
};

export default UploadQueueContext;
